window.VAE = window.VAE || {};

VAE.ConsistencyTester = (function () {
  'use strict';

  // WCAG criteria mapping for better analysis
  const WCAG_CRITERIA = {
    '1.1.1': 'Non-text Content',
    '1.3.1': 'Info and Relationships',
    '1.3.2': 'Meaningful Sequence',
    '1.3.3': 'Sensory Characteristics',
    '1.3.4': 'Orientation',
    '1.3.5': 'Identify Input Purpose',
    '1.4.1': 'Use of Color',
    '1.4.2': 'Audio Control',
    '1.4.3': 'Contrast (Minimum)',
    '1.4.4': 'Resize text',
    '1.4.5': 'Images of Text',
    '1.4.10': 'Reflow',
    '1.4.11': 'Non-text Contrast',
    '1.4.12': 'Text Spacing',
    '1.4.13': 'Content on Hover or Focus',
    '2.1.1': 'Keyboard',
    '2.1.2': 'No Keyboard Trap',
    '2.1.4': 'Character Key Shortcuts',
    '2.4.1': 'Bypass Blocks',
    '2.4.2': 'Page Titled',
    '2.4.3': 'Focus Order',
    '2.4.4': 'Link Purpose (In Context)',
    '2.4.5': 'Multiple Ways',
    '2.4.6': 'Headings and Labels',
    '2.4.7': 'Focus Visible',
    '2.5.1': 'Pointer Gestures',
    '2.5.2': 'Pointer Cancellation',
    '2.5.3': 'Label in Name',
    '2.5.4': 'Motion Actuation',
    '3.1.1': 'Language of Page',
    '3.1.2': 'Language of Parts',
    '3.2.1': 'On Focus',
    '3.2.2': 'On Input',
    '3.2.3': 'Consistent Navigation',
    '3.2.4': 'Consistent Identification',
    '3.3.1': 'Error Identification',
    '3.3.2': 'Labels or Instructions',
    '3.3.3': 'Error Suggestion',
    '3.3.4': 'Error Prevention (Legal, Financial, Data)',
    '4.1.1': 'Parsing',
    '4.1.2': 'Name, Role, Value',
    '4.1.3': 'Status Messages'
  };

  let testResults = [];
  let isRunning = false;

  // Extract WCAG criterion from issue
  function extractWcagCriterion(issue) {
    if (issue.wcagCriterion && issue.wcagCriterion !== 'unknown') {
      return issue.wcagCriterion;
    }
    if (issue.wcagRef) {
      return issue.wcagRef;
    }
    if (issue.tags && Array.isArray(issue.tags)) {
      const wcagTag = issue.tags.find(tag => tag.match(/^wcag\d+$/));
      if (wcagTag) {
        const numbers = wcagTag.replace('wcag', '');
        if (numbers.length >= 3) {
          return `${numbers[0]}.${numbers[1]}.${numbers.substring(2)}`;
        }
      }
    }
    return 'unknown';
  }

  // Extract VAE ID from issue (prioritize vaeId field, fallback to selector extraction)
  function extractVaeIdFromIssue(issue) {
    // Priority 1: Use vaeId directly from the issue object
    if (issue.vaeId) {
      return issue.vaeId;
    }
    
    // Priority 2: Try to extract from selector (root selector)
    if (issue.selector) {
      const match = issue.selector.match(/\[data-vae-id=['"]([^'"]+)['"]\]/);
      if (match) {
        return match[1];
      }
    }

    // Priority 3: Try to extract from actionParams.selector as fallback
    if (issue.actionParams && issue.actionParams.selector) {
      const match = issue.actionParams.selector.match(/\[data-vae-id=['"]([^'"]+)['"]\]/);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  // Analyze issues by WCAG criterion
  function analyzeIssuesByWcag(issues, source) {
    const wcagStats = {};
    
    issues.forEach(issue => {
      const wcag = extractWcagCriterion(issue);
      if (!wcagStats[wcag]) {
        wcagStats[wcag] = {
          criterion: wcag,
          title: WCAG_CRITERIA[wcag] || 'Unknown',
          count: 0,
          issues: [],
          source: source
        };
      }
      wcagStats[wcag].count++;
      wcagStats[wcag].issues.push({
        type: issue.type || issue.id || 'unknown',
        impact: issue.impact || 'unknown',
        selector: issue.selector || 'unknown',
        message: issue.message || issue.description || 'No description'
      });
    });

    return wcagStats;
  }

  // Clear cache to ensure fresh analysis
  function clearAnalysisCache() {
    try {
      const pagePathname = window.location.pathname;
      const pathnameHash = simpleStringHash(pagePathname);
      const resultsCacheKey = `vae_analysis_results_cache_${pathnameHash}`;
      const contentHashCacheKey = `vae_content_hash_${pathnameHash}`;
      
      localStorage.removeItem(resultsCacheKey);
      localStorage.removeItem(contentHashCacheKey);
      
      console.log(`VAE.ConsistencyTester: Cleared cache for hash ${pathnameHash}`);
    } catch (error) {
      console.warn('VAE.ConsistencyTester: Error clearing cache:', error);
    }
  }

  // Simple string hash function (same as in core.js)
  function simpleStringHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    return hash.toString(36);
  }

  // Run a single analysis iteration
  async function runSingleAnalysis(iteration) {
    console.log(`VAE.ConsistencyTester: Starting analysis iteration ${iteration}`);
    
    // Clear cache before each iteration to ensure fresh analysis
    clearAnalysisCache();
    
    const startTime = Date.now();
    const result = {
      iteration: iteration,
      timestamp: new Date().toISOString(),
      axeResults: null,
      llmResults: null,
      error: null,
      duration: 0
    };

    try {
      // 1. Run axe-core analysis
      console.log(`VAE.ConsistencyTester: Running axe-core analysis (iteration ${iteration})`);
      const axeAnalysis = await VAE.AxeIntegration.runAxeAnalysis();
      const axeTransformed = VAE.AxeIntegration.transformAxeResults(axeAnalysis);
      
      result.axeResults = {
        violationsCount: axeAnalysis.violations.length,
        transformedCount: axeTransformed.length,
        wcagStats: analyzeIssuesByWcag(axeTransformed, 'axe-core'),
        rawViolations: axeAnalysis.violations.map(v => ({
          id: v.id,
          impact: v.impact,
          tags: v.tags,
          nodes: v.nodes.length
        })),
        detailedIssues: axeTransformed.map(issue => ({
          wcagCriterion: extractWcagCriterion(issue),
          type: issue.type || issue.id || 'unknown',
          impact: issue.impact || 'unknown',
          selector: issue.selector || 'unknown',
          vaeId: issue.vaeId, // Axe-core issues have vaeId directly
          message: issue.message || issue.description || 'No description',
          source: 'axe-core'
        }))
      };

      // 2. Run LLM analysis with cache-busting parameters
      console.log(`VAE.ConsistencyTester: Running LLM analysis (iteration ${iteration})`);
      const pageHtml = document.documentElement.outerHTML;
      
      // Add cache-busting parameters to ensure fresh analysis
      const llmAnalysis = await VAE.API.fetchAAIInitialData({ 
        htmlContent: pageHtml, 
        pageUrl: window.location.href + `?consistency_test=${iteration}&timestamp=${Date.now()}`,
        axeResults: axeTransformed, // Include axe results as widget does
        forceRefresh: true, // Flag to indicate this should bypass any server-side caching
        consistencyTest: true // Flag to indicate this is a consistency test
      });

      // DEBUG: Log the complete LLM analysis response
      console.log(`VAE.ConsistencyTester: LLM Analysis Response (iteration ${iteration}):`, llmAnalysis);
      console.log(`VAE.ConsistencyTester: LLM Analysis Response type:`, typeof llmAnalysis);
      console.log(`VAE.ConsistencyTester: LLM Analysis Response keys:`, llmAnalysis ? Object.keys(llmAnalysis) : 'null/undefined');

      if (llmAnalysis && llmAnalysis.issues && Array.isArray(llmAnalysis.issues)) {
        console.log(`VAE.ConsistencyTester: Direct LLM issues found: ${llmAnalysis.issues.length}`);
        console.log(`VAE.ConsistencyTester: Sample LLM issues:`, llmAnalysis.issues.slice(0, 3));
        
        // DEBUG: Check vaeId structure in LLM issues
        console.log('VAE.ConsistencyTester: DEBUG - LLM Issue vaeId analysis:');
        llmAnalysis.issues.slice(0, 5).forEach((issue, index) => {
          console.log(`Issue ${index}:`, {
            vaeId: issue.vaeId,
            selector: issue.selector,
            actionParams: issue.actionParams,
            issueId: issue.issueId,
            type: issue.type
          });
        });

        result.llmResults = {
          issuesCount: llmAnalysis.issues.length,
          wcagStats: analyzeIssuesByWcag(llmAnalysis.issues, 'llm'),
          taskId: llmAnalysis.taskId || null,
          metadata: llmAnalysis.metadata || null,
          detailedIssues: llmAnalysis.issues.map(issue => {
            // LLM issues don't have direct vaeId field, always extract from selector
            const finalVaeId = extractVaeIdFromIssue(issue);

            // DEBUG: Log vaeId extraction for each issue
            console.log(`VAE.ConsistencyTester: Issue ${issue.issueId || 'unknown'} - vaeId: ${issue.vaeId}, extracted: ${extractVaeIdFromIssue(issue)}, final: ${finalVaeId}`);

            return {
              wcagCriterion: extractWcagCriterion(issue),
              type: issue.type || issue.id || 'unknown',
              impact: issue.impact || 'unknown',
              selector: issue.selector || 'unknown',
              vaeId: finalVaeId, // For LLM issues, always use extraction
              message: issue.message || issue.description || 'No description',
              source: 'llm',
              confidence: issue.confidence || null,
              explanation: issue.explanation || null
            };
          })
        };
      } else if (llmAnalysis && llmAnalysis.taskId) {
        // Handle async task - wait for completion
        console.log(`VAE.ConsistencyTester: LLM returned taskId ${llmAnalysis.taskId}, waiting for completion...`);
        
        const taskResults = await waitForTaskCompletion(llmAnalysis.taskId);
        
        // DEBUG: Log the task completion results
        console.log(`VAE.ConsistencyTester: Task completion results (iteration ${iteration}):`, taskResults);
        console.log(`VAE.ConsistencyTester: Task results type:`, typeof taskResults);
        console.log(`VAE.ConsistencyTester: Task results keys:`, taskResults ? Object.keys(taskResults) : 'null/undefined');
        
        // DEBUG: Detailed check of taskResults structure
        if (taskResults) {
          console.log(`VAE.ConsistencyTester: taskResults.issues exists:`, !!taskResults.issues);
          console.log(`VAE.ConsistencyTester: taskResults.issues type:`, typeof taskResults.issues);
          console.log(`VAE.ConsistencyTester: taskResults.issues length:`, taskResults.issues ? taskResults.issues.length : 'N/A');
          console.log(`VAE.ConsistencyTester: taskResults.total_issues_found:`, taskResults.total_issues_found);
          
          if (taskResults.issues && Array.isArray(taskResults.issues)) {
            console.log(`VAE.ConsistencyTester: taskResults.issues is array with ${taskResults.issues.length} items`);
            if (taskResults.issues.length > 0) {
              console.log(`VAE.ConsistencyTester: First issue structure:`, taskResults.issues[0]);
            }
          } else {
            console.log(`VAE.ConsistencyTester: taskResults.issues is not an array or is null/undefined`);
          }
        }
        
        if (taskResults && taskResults.issues) {
          console.log(`VAE.ConsistencyTester: Task completion issues found: ${taskResults.issues.length}`);
          console.log(`VAE.ConsistencyTester: Sample task issues:`, taskResults.issues.slice(0, 3));
          
          // DEBUG: Check vaeId structure in task completion LLM issues
          console.log('VAE.ConsistencyTester: DEBUG - Task completion LLM Issue vaeId analysis:');
          taskResults.issues.slice(0, 5).forEach((issue, index) => {
            console.log(`Task Issue ${index}:`, {
              vaeId: issue.vaeId,
              selector: issue.selector,
              actionParams: issue.actionParams,
              issueId: issue.issueId,
              type: issue.type
            });
          });

          result.llmResults = {
            issuesCount: taskResults.issues.length,
            wcagStats: analyzeIssuesByWcag(taskResults.issues, 'llm'),
            taskId: llmAnalysis.taskId,
            metadata: taskResults.metadata || null,
            detailedIssues: taskResults.issues.map(issue => {
              // LLM issues don't have direct vaeId field, always extract from selector
              const finalVaeId = extractVaeIdFromIssue(issue);

              // DEBUG: Log vaeId extraction for each task issue
              console.log(`VAE.ConsistencyTester: Task Issue ${issue.issueId || 'unknown'} - vaeId: ${issue.vaeId}, extracted: ${extractVaeIdFromIssue(issue)}, final: ${finalVaeId}`);

              return {
                wcagCriterion: extractWcagCriterion(issue),
                type: issue.type || issue.id || 'unknown',
                impact: issue.impact || 'unknown',
                selector: issue.selector || 'unknown',
                vaeId: finalVaeId, // For LLM issues, always use extraction
                message: issue.message || issue.description || 'No description',
                source: 'llm',
                confidence: issue.confidence || null,
                explanation: issue.explanation || null
              };
            })
          };
        } else {
          console.error(`VAE.ConsistencyTester: Task completed but no issues found. TaskResults structure:`, taskResults);
          throw new Error('LLM task completed but no issues returned');
        }
      } else {
        console.error(`VAE.ConsistencyTester: Invalid LLM response format. Response:`, llmAnalysis);
        throw new Error('Invalid LLM response format');
      }

      result.duration = Date.now() - startTime;
      console.log(`VAE.ConsistencyTester: Iteration ${iteration} completed in ${result.duration}ms`);
      console.log(`VAE.ConsistencyTester: Final result summary - Axe: ${result.axeResults?.transformedCount || 0}, LLM: ${result.llmResults?.issuesCount || 0}`);
      
    } catch (error) {
      console.error(`VAE.ConsistencyTester: Error in iteration ${iteration}:`, error);
      result.error = error.message;
      result.duration = Date.now() - startTime;
    }

    return result;
  }

  // Wait for LLM task completion with extended timeout for consistency tests
  async function waitForTaskCompletion(taskId, timeout = 300000) { // Increased to 5 minutes
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      let sseConnection = null;

      const cleanup = () => {
        if (sseConnection && typeof sseConnection.close === 'function') {
          sseConnection.close();
        }
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`Task ${taskId} timeout after ${timeout}ms (${Math.round(timeout/1000)}s)`));
      }, timeout);

      try {
        sseConnection = VAE.API.subscribeToSSEUpdates(taskId, {
          events: {
            task_complete: async (data) => {
              clearTimeout(timeoutId);
              cleanup();
              
              // DEBUG: Log the complete SSE task_complete data
              console.log(`VAE.ConsistencyTester: SSE task_complete data for ${taskId}:`, data);
              console.log(`VAE.ConsistencyTester: SSE data type:`, typeof data);
              console.log(`VAE.ConsistencyTester: SSE data keys:`, data ? Object.keys(data) : 'null/undefined');
              
              if (data && data.task) {
                const taskStatus = data.task.status;
                console.log(`VAE.ConsistencyTester: Task ${taskId} completed with status: ${taskStatus}`);
                console.log(`VAE.ConsistencyTester: Task data:`, data.task);
                
                if (taskStatus === 'completed' || taskStatus === 'completed_partial') {
                  try {
                    const pageId = data.task.page_id;
                    console.log(`VAE.ConsistencyTester: Extracted page_id: ${pageId}`);
                    
                    if (pageId) {
                      // Add a small delay to ensure issues are fully written to database
                      console.log(`VAE.ConsistencyTester: Waiting 2 seconds for database write completion...`);
                      await new Promise(resolve => setTimeout(resolve, 2000));
                      
                      const pageResults = await VAE.API.fetchPageResults(pageId);
                      
                      // DEBUG: Log the complete page results
                      console.log(`VAE.ConsistencyTester: Page results for ${pageId}:`, pageResults);
                      console.log(`VAE.ConsistencyTester: Page results type:`, typeof pageResults);
                      console.log(`VAE.ConsistencyTester: Page results keys:`, pageResults ? Object.keys(pageResults) : 'null/undefined');
                      
                      if (pageResults && pageResults.issues) {
                        console.log(`VAE.ConsistencyTester: Page results issues count: ${pageResults.issues.length}`);
                        console.log(`VAE.ConsistencyTester: Sample page results issues:`, pageResults.issues.slice(0, 3));
                        
                        // DEBUG: Check if issues are being properly assigned
                        console.log(`VAE.ConsistencyTester: About to resolve with pageResults containing ${pageResults.issues.length} issues`);
                      } else {
                        console.warn(`VAE.ConsistencyTester: Page results has no issues array. Available keys:`, pageResults ? Object.keys(pageResults) : 'none');
                      }
                      
                      // DEBUG: Check Supabase database state
                      await debugSupabaseState(taskId, pageId);
                      
                      // Make sure we return the pageResults with the issues
                      resolve(pageResults);
                    } else {
                      reject(new Error('No page_id in task completion'));
                    }
                  } catch (fetchError) {
                    console.error(`VAE.ConsistencyTester: Error fetching page results for ${taskId}:`, fetchError);
                    reject(fetchError);
                  }
                } else if (taskStatus === 'failed') {
                  const errorMsg = data.task.error || 'Task failed without specific error message';
                  reject(new Error(`Task failed: ${errorMsg}`));
                } else if (taskStatus === 'timeout') {
                  reject(new Error('Analysis timeout - content too complex'));
                } else {
                  reject(new Error(`Task completed with unexpected status: ${taskStatus}`));
                }
              } else {
                console.error(`VAE.ConsistencyTester: Invalid task completion data:`, data);
                reject(new Error('Invalid task completion data received'));
              }
            },
            error: (errorData) => {
              clearTimeout(timeoutId);
              cleanup();
              console.error(`VAE.ConsistencyTester: SSE error for ${taskId}:`, errorData);
              reject(new Error(`SSE error: ${errorData?.message || 'Connection error'}`));
            }
          }
        });

        if (sseConnection && sseConnection.error) {
          clearTimeout(timeoutId);
          reject(new Error(`SSE connection failed: ${sseConnection.error}`));
        }

      } catch (error) {
        clearTimeout(timeoutId);
        cleanup();
        reject(error);
      }
    });
  }

  // Calculate statistics across all iterations
  function calculateStatistics(results) {
    const validResults = results.filter(r => !r.error);
    
    if (validResults.length === 0) {
      return { error: 'No valid results to analyze' };
    }

    // Collect all WCAG criteria found across all iterations
    const allWcagCriteria = new Set();
    validResults.forEach(result => {
      if (result.axeResults) {
        Object.keys(result.axeResults.wcagStats).forEach(wcag => allWcagCriteria.add(wcag));
      }
      if (result.llmResults) {
        Object.keys(result.llmResults.wcagStats).forEach(wcag => allWcagCriteria.add(wcag));
      }
    });

    // Calculate statistics for each WCAG criterion
    const wcagAnalysis = {};
    allWcagCriteria.forEach(wcag => {
      const axeCounts = [];
      const llmCounts = [];
      const totalCounts = [];

      validResults.forEach(result => {
        const axeCount = result.axeResults?.wcagStats[wcag]?.count || 0;
        const llmCount = result.llmResults?.wcagStats[wcag]?.count || 0;
        const totalCount = axeCount + llmCount;

        axeCounts.push(axeCount);
        llmCounts.push(llmCount);
        totalCounts.push(totalCount);
      });

      const axeStats = calculateArrayStats(axeCounts);
      const llmStats = calculateArrayStats(llmCounts);
      const totalStats = calculateArrayStats(totalCounts);

      wcagAnalysis[wcag] = {
        criterion: wcag,
        title: WCAG_CRITERIA[wcag] || 'Unknown',
        axe: axeStats,
        llm: llmStats,
        total: totalStats,
        variance: {
          axe: axeStats.max - axeStats.min,
          llm: llmStats.max - llmStats.min,
          total: totalStats.max - totalStats.min
        }
      };
    });

    // Overall statistics
    const axeTotalCounts = validResults.map(r => r.axeResults?.transformedCount || 0);
    const llmTotalCounts = validResults.map(r => r.llmResults?.issuesCount || 0);
    const combinedTotalCounts = validResults.map(r => 
      (r.axeResults?.transformedCount || 0) + (r.llmResults?.issuesCount || 0)
    );

    return {
      totalIterations: results.length,
      validIterations: validResults.length,
      errorIterations: results.length - validResults.length,
      
      overallStats: {
        axe: calculateArrayStats(axeTotalCounts),
        llm: calculateArrayStats(llmTotalCounts),
        combined: calculateArrayStats(combinedTotalCounts)
      },
      
      wcagAnalysis: wcagAnalysis,
      
      // Find criteria with highest variance
      mostVariableCriteria: Object.values(wcagAnalysis)
        .sort((a, b) => b.variance.total - a.variance.total)
        .slice(0, 10),
      
      // Summary
      summary: {
        totalWcagCriteria: allWcagCriteria.size,
        averageAxeIssues: Math.round(axeTotalCounts.reduce((a, b) => a + b, 0) / axeTotalCounts.length),
        averageLlmIssues: Math.round(llmTotalCounts.reduce((a, b) => a + b, 0) / llmTotalCounts.length),
        averageTotalIssues: Math.round(combinedTotalCounts.reduce((a, b) => a + b, 0) / combinedTotalCounts.length),
        maxVarianceAxe: Math.max(...axeTotalCounts) - Math.min(...axeTotalCounts),
        maxVarianceLlm: Math.max(...llmTotalCounts) - Math.min(...llmTotalCounts),
        maxVarianceTotal: Math.max(...combinedTotalCounts) - Math.min(...combinedTotalCounts)
      },

      // VAE ID overlap analysis across all iterations
      vaeIdAnalysis: (() => {
        const allOverlaps = [];
        const allAxeVaeIds = new Set();
        const allLlmVaeIds = new Set();
        
        validResults.forEach(result => {
          const axeIssues = result.axeResults?.detailedIssues || [];
          const llmIssues = result.llmResults?.detailedIssues || [];
          const overlap = analyzeVaeIdOverlaps(axeIssues, llmIssues);
          
          allOverlaps.push(overlap);
          overlap.axeVaeIds.forEach(id => allAxeVaeIds.add(id));
          overlap.llmVaeIds.forEach(id => allLlmVaeIds.add(id));
        });
        
        const overlapPercentages = allOverlaps.map(o => o.overlapPercentage);
        const avgOverlapPercentage = overlapPercentages.length > 0 ? 
          Math.round(overlapPercentages.reduce((a, b) => a + b, 0) / overlapPercentages.length) : 0;
        
        return {
          averageOverlapPercentage: avgOverlapPercentage,
          minOverlapPercentage: Math.min(...overlapPercentages),
          maxOverlapPercentage: Math.max(...overlapPercentages),
          totalUniqueAxeVaeIds: allAxeVaeIds.size,
          totalUniqueLlmVaeIds: allLlmVaeIds.size,
          iterationOverlaps: allOverlaps
        };
      })()
    };
  }

  // Calculate basic statistics for an array of numbers
  function calculateArrayStats(arr) {
    if (arr.length === 0) return { min: 0, max: 0, avg: 0, median: 0 };
    
    const sorted = [...arr].sort((a, b) => a - b);
    const sum = arr.reduce((a, b) => a + b, 0);
    
    return {
      min: Math.min(...arr),
      max: Math.max(...arr),
      avg: Math.round((sum / arr.length) * 100) / 100,
      median: sorted.length % 2 === 0 
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)]
    };
  }

  // Generate HTML report
  function generateHTMLReport(statistics, testResults = []) {
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 1200px; margin: 0 auto; padding: 20px;">
        <h1>VAE Consistency Test Report</h1>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>URL:</strong> ${window.location.href}</p>
        
        <div style="background: #f8f9fa; padding: 1rem; border-radius: 0.5rem; margin: 1rem 0;">
          <h3 style="margin-top: 0;">📋 Navigation</h3>
          <ul style="margin-bottom: 0; columns: 2; column-gap: 2rem;">
            <li><a href="#summary">Summary</a></li>
            <li><a href="#overall-stats">Overall Statistics</a></li>
            <li><a href="#variance-analysis">Highest Variance</a></li>
            <li><a href="#matrix-view">Matrix-Ansicht</a></li>
            <li><a href="#vaeid-analysis">Element-Overlap-Analyse</a></li>
            <li><a href="#wcag-analysis">WCAG Analysis</a></li>
            <li><a href="#iteration-details">Iteration Details</a></li>
          </ul>
        </div>
        
        <h2 id="summary">Summary</h2>
        <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
          <tr><td>Total Iterations</td><td>${statistics.totalIterations}</td></tr>
          <tr><td>Valid Iterations</td><td>${statistics.validIterations}</td></tr>
          <tr><td>Error Iterations</td><td>${statistics.errorIterations}</td></tr>
          <tr><td>Success Rate</td><td>${Math.round((statistics.validIterations / statistics.totalIterations) * 100)}%</td></tr>
          <tr><td>Total WCAG Criteria Found</td><td>${statistics.summary.totalWcagCriteria}</td></tr>
          <tr><td>Average Axe-core Issues</td><td>${statistics.summary.averageAxeIssues}</td></tr>
          <tr><td>Average LLM Issues</td><td>${statistics.summary.averageLlmIssues}</td></tr>
          <tr><td>Average Total Issues</td><td>${statistics.summary.averageTotalIssues}</td></tr>
          <tr><td>Max Variance (Axe-core)</td><td>${statistics.summary.maxVarianceAxe}</td></tr>
          <tr><td>Max Variance (LLM)</td><td>${statistics.summary.maxVarianceLlm}</td></tr>
          <tr><td>Max Variance (Total)</td><td>${statistics.summary.maxVarianceTotal}</td></tr>
          <tr><td>Average Element Overlap</td><td>${statistics.vaeIdAnalysis.averageOverlapPercentage}%</td></tr>
          <tr><td>Unique Elements (Axe-core)</td><td>${statistics.vaeIdAnalysis.totalUniqueAxeVaeIds}</td></tr>
          <tr><td>Unique Elements (LLM)</td><td>${statistics.vaeIdAnalysis.totalUniqueLlmVaeIds}</td></tr>
        </table>

        ${statistics.errorIterations > 0 ? `
        <h2>Error Analysis</h2>
        <p><strong>Note:</strong> ${statistics.errorIterations} iteration(s) failed. Common causes:</p>
        <ul>
          <li><strong>Analysis timeout:</strong> Content too complex for LLM analysis</li>
          <li><strong>Network issues:</strong> Connection problems during analysis</li>
          <li><strong>Server overload:</strong> API temporarily unavailable</li>
        </ul>
        <p>Consider running the test again or reducing the complexity of the page content.</p>
        ` : ''}

        <h2 id="overall-stats">Overall Statistics</h2>
        <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
          <tr>
            <th>Source</th>
            <th>Min</th>
            <th>Max</th>
            <th>Average</th>
            <th>Median</th>
            <th>Variance</th>
          </tr>
          <tr>
            <td>Axe-core</td>
            <td>${statistics.overallStats.axe.min}</td>
            <td>${statistics.overallStats.axe.max}</td>
            <td>${statistics.overallStats.axe.avg}</td>
            <td>${statistics.overallStats.axe.median}</td>
            <td>${statistics.overallStats.axe.max - statistics.overallStats.axe.min}</td>
          </tr>
          <tr>
            <td>LLM</td>
            <td>${statistics.overallStats.llm.min}</td>
            <td>${statistics.overallStats.llm.max}</td>
            <td>${statistics.overallStats.llm.avg}</td>
            <td>${statistics.overallStats.llm.median}</td>
            <td>${statistics.overallStats.llm.max - statistics.overallStats.llm.min}</td>
          </tr>
          <tr>
            <td>Combined</td>
            <td>${statistics.overallStats.combined.min}</td>
            <td>${statistics.overallStats.combined.max}</td>
            <td>${statistics.overallStats.combined.avg}</td>
            <td>${statistics.overallStats.combined.median}</td>
            <td>${statistics.overallStats.combined.max - statistics.overallStats.combined.min}</td>
          </tr>
        </table>

        <h2 id="variance-analysis">WCAG Criteria with Highest Variance</h2>
        <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
          <tr>
            <th>WCAG Criterion</th>
            <th>Title</th>
            <th>Axe Variance</th>
            <th>LLM Variance</th>
            <th>Total Variance</th>
            <th>Axe Avg</th>
            <th>LLM Avg</th>
          </tr>
          ${statistics.mostVariableCriteria.map(criteria => `
            <tr>
              <td>${criteria.criterion}</td>
              <td>${criteria.title}</td>
              <td>${criteria.variance.axe}</td>
              <td>${criteria.variance.llm}</td>
              <td>${criteria.variance.total}</td>
              <td>${criteria.axe.avg}</td>
              <td>${criteria.llm.avg}</td>
            </tr>
          `).join('')}
        </table>

        <h2 id="matrix-view">WCAG-Kriterien vs. Iterationen Matrix</h2>
        ${generateWcagIterationMatrix(testResults)}

        <h2 id="vaeid-analysis">Element-Overlap-Analyse</h2>
        ${generateVaeIdAnalysisHTML(statistics.vaeIdAnalysis)}

        <h2 id="wcag-analysis">Detailed WCAG Analysis</h2>
        <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
          <tr>
            <th>WCAG</th>
            <th>Title</th>
            <th colspan="4">Axe-core (Min/Max/Avg/Median)</th>
            <th colspan="4">LLM (Min/Max/Avg/Median)</th>
            <th colspan="4">Total (Min/Max/Avg/Median)</th>
          </tr>
          ${Object.values(statistics.wcagAnalysis)
            .sort((a, b) => a.criterion.localeCompare(b.criterion))
            .map(criteria => `
            <tr>
              <td>${criteria.criterion}</td>
              <td>${criteria.title}</td>
              <td>${criteria.axe.min}</td>
              <td>${criteria.axe.max}</td>
              <td>${criteria.axe.avg}</td>
              <td>${criteria.axe.median}</td>
              <td>${criteria.llm.min}</td>
              <td>${criteria.llm.max}</td>
              <td>${criteria.llm.avg}</td>
              <td>${criteria.llm.median}</td>
              <td>${criteria.total.min}</td>
              <td>${criteria.total.max}</td>
              <td>${criteria.total.avg}</td>
              <td>${criteria.total.median}</td>
            </tr>
          `).join('')}
        </table>

        <h2 id="iteration-details">Detailed Issues by Iteration</h2>
        <div style="background: #e8f4fd; padding: 1rem; border-radius: 0.5rem; margin-bottom: 1rem;">
          <h4 style="margin-top: 0;">💡 How to interpret the results:</h4>
          <ul style="margin-bottom: 0;">
            <li><strong>Consistent Issues:</strong> Issues found in multiple iterations indicate reliable detection</li>
            <li><strong>Inconsistent Issues:</strong> Issues found only in some iterations may indicate analysis variability</li>
            <li><strong>LLM vs Axe-core:</strong> Compare which tool finds which issues consistently</li>
            <li><strong>Confidence Scores:</strong> LLM confidence levels can help assess reliability</li>
          </ul>
        </div>
        ${generateIterationDetailsHTML(testResults)}
      </div>
    `;

    return html;
  }

  // Generate detailed HTML for each iteration's issues
  function generateIterationDetailsHTML(results) {
    const validResults = results.filter(r => !r.error);
    
    if (validResults.length === 0) {
      return '<p>No valid iterations to display.</p>';
    }

    return validResults.map(result => {
      const axeIssues = result.axeResults?.detailedIssues || [];
      const llmIssues = result.llmResults?.detailedIssues || [];
      const allIssues = [...axeIssues, ...llmIssues];
      
      // Analyze VAE ID overlaps
      const overlapAnalysis = analyzeVaeIdOverlaps(axeIssues, llmIssues);

      return `
        <div style="margin-bottom: 2rem; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
          <div style="background: #f8f9fa; padding: 1rem; border-bottom: 1px solid #ddd;">
            <h3 style="margin: 0; color: #333;">
              Iteration ${result.iteration} 
              <span style="font-size: 0.8em; color: #666;">
                (${result.timestamp}) - Duration: ${Math.round(result.duration/1000)}s
              </span>
            </h3>
            <div style="margin-top: 0.5rem; font-size: 0.9em; color: #555;">
              <strong>Total Issues:</strong> ${allIssues.length} | 
              <strong>Axe-core:</strong> ${axeIssues.length} | 
              <strong>LLM:</strong> ${llmIssues.length}
            </div>
            
            ${axeIssues.length > 0 && llmIssues.length > 0 ? `
              <div style="margin-top: 0.5rem; padding: 0.5rem; background: #e3f2fd; border-radius: 4px; font-size: 0.85em;">
                <strong>🔍 VAE-ID Overlap-Analyse:</strong>
                <div style="margin-top: 0.25rem;">
                  <span style="color: #1976d2;">
                    <strong>${overlapAnalysis.overlap.length}</strong> gemeinsame Elemente (${overlapAnalysis.overlapPercentage}% Überschneidung)
                  </span> | 
                  <span style="color: #f57c00;">
                    <strong>${overlapAnalysis.axeOnly.length}</strong> nur Axe-core
                  </span> | 
                  <span style="color: #388e3c;">
                    <strong>${overlapAnalysis.llmOnly.length}</strong> nur LLM
                  </span>
                </div>
                ${overlapAnalysis.overlap.length > 0 ? `
                  <div style="margin-top: 0.25rem; font-size: 0.8em; color: #666;">
                    Gemeinsame VAE-IDs: ${overlapAnalysis.overlap.slice(0, 5).join(', ')}${overlapAnalysis.overlap.length > 5 ? ` (+${overlapAnalysis.overlap.length - 5} weitere)` : ''}
                  </div>
                ` : ''}
              </div>
            ` : ''}
          </div>
          
          <div style="padding: 1rem;">
            ${allIssues.length === 0 ? 
              '<p style="color: #28a745; font-weight: 500;">✅ No accessibility issues found in this iteration</p>' :
              generateIssuesTableHTML(allIssues)
            }
          </div>
        </div>
      `;
    }).join('');
  }

  // Generate HTML table for issues
  function generateIssuesTableHTML(issues) {
    // Group issues by WCAG criterion
    const groupedIssues = {};
    issues.forEach(issue => {
      const wcag = issue.wcagCriterion || 'unknown';
      if (!groupedIssues[wcag]) {
        groupedIssues[wcag] = [];
      }
      groupedIssues[wcag].push(issue);
    });

    // Create sets of VAE IDs for overlap detection
    const axeVaeIds = new Set();
    const llmVaeIds = new Set();
    
    issues.forEach(issue => {
      if (issue.vaeId) {
        if (issue.source === 'axe-core') {
          axeVaeIds.add(issue.vaeId);
        } else if (issue.source === 'llm') {
          llmVaeIds.add(issue.vaeId);
        }
      }
    });

    const tablesHTML = Object.entries(groupedIssues).map(([wcag, wcagIssues]) => {
      const wcagTitle = WCAG_CRITERIA[wcag] || 'Unknown';
      
      // Create sets of VAE IDs for overlap detection WITHIN THIS WCAG CRITERION
      const wcagAxeVaeIds = new Set();
      const wcagLlmVaeIds = new Set();
      
      wcagIssues.forEach(issue => {
        if (issue.vaeId) {
          if (issue.source === 'axe-core') {
            wcagAxeVaeIds.add(issue.vaeId);
          } else if (issue.source === 'llm') {
            wcagLlmVaeIds.add(issue.vaeId);
          }
        }
      });
      
      return `
        <div style="margin-bottom: 1.5rem;">
          <h4 style="margin: 0 0 0.5rem 0; color: #495057; font-size: 1rem;">
            ${wcag} - ${wcagTitle} 
            <span style="background: #e9ecef; padding: 0.2rem 0.5rem; border-radius: 0.25rem; font-size: 0.8rem; margin-left: 0.5rem;">
              ${wcagIssues.length} issue${wcagIssues.length !== 1 ? 's' : ''}
            </span>
          </h4>
          
          <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 0.85rem;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="text-align: left; width: 80px;">Source</th>
                <th style="text-align: left; width: 80px;">Impact</th>
                <th style="text-align: left; width: 120px;">Type</th>
                <th style="text-align: left;">Message</th>
                <th style="text-align: left; width: 100px;">VAE-ID</th>
                <th style="text-align: left; width: 150px;">Selector</th>
                ${wcagIssues.some(i => i.confidence) ? '<th style="text-align: left; width: 80px;">Confidence</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${wcagIssues.map(issue => {
                // Check if this VAE-ID appears in both axe-core and LLM results WITHIN THIS WCAG CRITERION
                const isOverlapping = issue.vaeId && wcagAxeVaeIds.has(issue.vaeId) && wcagLlmVaeIds.has(issue.vaeId);
                const isLlmWithOverlap = issue.source === 'llm' && isOverlapping;
                const isAxeWithOverlap = issue.source === 'axe-core' && isOverlapping;
                
                return `
                <tr>
                  <td>
                    <span style="background: ${issue.source === 'axe-core' ? '#e3f2fd' : '#f3e5f5'}; 
                                 color: ${issue.source === 'axe-core' ? '#1976d2' : '#7b1fa2'}; 
                                 padding: 0.2rem 0.4rem; border-radius: 0.25rem; font-size: 0.75rem; font-weight: 500;">
                      ${issue.source}
                    </span>
                    ${isOverlapping ? `
                      <div style="margin-top: 0.2rem;">
                        <span style="background: #4caf50; color: white; padding: 0.1rem 0.3rem; border-radius: 0.2rem; font-size: 0.7rem; font-weight: 500;">
                          ⚡ OVERLAP
                        </span>
                      </div>
                    ` : ''}
                  </td>
                  <td>
                    <span style="background: ${getImpactColor(issue.impact)}; 
                                 color: white; padding: 0.2rem 0.4rem; border-radius: 0.25rem; font-size: 0.75rem; font-weight: 500;">
                      ${issue.impact}
                    </span>
                  </td>
                  <td style="font-family: monospace; font-size: 0.8rem;">${issue.type}</td>
                  <td style="line-height: 1.4;">
                    ${issue.message}
                    ${issue.explanation ? `<br><small style="color: #666; font-style: italic;">${issue.explanation}</small>` : ''}
                  </td>
                  <td style="font-family: monospace; font-size: 0.8rem; font-weight: 500; ${issue.vaeId ? 'color: #1976d2;' : 'color: #999;'} position: relative;">
                    ${issue.vaeId ? `
                      <span style="
                        ${isLlmWithOverlap ? `
                          background: linear-gradient(45deg, #4caf50, #66bb6a);
                          color: white;
                          padding: 0.2rem 0.4rem;
                          border-radius: 0.3rem;
                          border: 2px solid #4caf50;
                          box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
                          font-weight: bold;
                        ` : isAxeWithOverlap ? `
                          background: linear-gradient(45deg, #2196f3, #42a5f5);
                          color: white;
                          padding: 0.2rem 0.4rem;
                          border-radius: 0.3rem;
                          border: 2px solid #2196f3;
                          box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
                          font-weight: bold;
                        ` : ''}
                      ">
                        ${issue.vaeId}
                      </span>
                      ${isOverlapping ? `
                        <div style="position: absolute; top: -8px; right: -8px; width: 16px; height: 16px; background: #ff9800; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; color: white; font-weight: bold; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);">
                          ⚡
                        </div>
                      ` : ''}
                    ` : '—'}
                  </td>
                  <td style="font-family: monospace; font-size: 0.75rem; word-break: break-all; max-width: 150px;">
                    ${issue.selector.length > 50 ? issue.selector.substring(0, 50) + '...' : issue.selector}
                  </td>
                  ${issue.confidence ? `<td style="text-align: center;">${Math.round(issue.confidence * 100)}%</td>` : ''}
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
        </div>
      `;
    }).join('');

    // Add legend if there are any overlapping VAE IDs within any WCAG criterion
    let hasAnyOverlaps = false;
    Object.entries(groupedIssues).forEach(([wcag, wcagIssues]) => {
      const wcagAxeVaeIds = new Set();
      const wcagLlmVaeIds = new Set();
      
      wcagIssues.forEach(issue => {
        if (issue.vaeId) {
          if (issue.source === 'axe-core') {
            wcagAxeVaeIds.add(issue.vaeId);
          } else if (issue.source === 'llm') {
            wcagLlmVaeIds.add(issue.vaeId);
          }
        }
      });
      
      // Check if there are overlaps within this WCAG criterion
      const hasOverlapInThisCriterion = wcagAxeVaeIds.size > 0 && wcagLlmVaeIds.size > 0 && 
        [...wcagAxeVaeIds].some(id => wcagLlmVaeIds.has(id));
      
      if (hasOverlapInThisCriterion) {
        hasAnyOverlaps = true;
      }
    });
    
    const legendHTML = hasAnyOverlaps ? `
      <!-- VAE-ID Overlap Legend -->
      <div style="background: #f8f9fa; padding: 1rem; border-radius: 0.5rem; margin-top: 1.5rem; border-left: 4px solid #007bff;">
        <h4 style="margin-top: 0; color: #495057; font-size: 0.9rem;">🏷️ VAE-ID Markierungen Legende:</h4>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; font-size: 0.85rem;">
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span style="background: linear-gradient(45deg, #4caf50, #66bb6a); color: white; padding: 0.2rem 0.4rem; border-radius: 0.3rem; border: 2px solid #4caf50; font-family: monospace; font-size: 0.8rem; font-weight: bold;">
              vae-123
            </span>
            <span>LLM Issue mit Overlap</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span style="background: linear-gradient(45deg, #2196f3, #42a5f5); color: white; padding: 0.2rem 0.4rem; border-radius: 0.3rem; border: 2px solid #2196f3; font-family: monospace; font-size: 0.8rem; font-weight: bold;">
              vae-123
            </span>
            <span>Axe-core Issue mit Overlap</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span style="background: #4caf50; color: white; padding: 0.1rem 0.3rem; border-radius: 0.2rem; font-size: 0.7rem; font-weight: 500;">
              ⚡ OVERLAP
            </span>
            <span>Element von beiden Tools gefunden</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span style="color: #1976d2; font-family: monospace; font-size: 0.8rem; font-weight: 500;">
              vae-123
            </span>
            <span>Standard VAE-ID (kein Overlap)</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span style="color: #999; font-family: monospace; font-size: 0.8rem;">
              —
            </span>
            <span>Keine VAE-ID verfügbar</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <div style="position: relative; display: inline-block;">
              <span style="background: #ff9800; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: white; font-weight: bold; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);">
                ⚡
              </span>
            </div>
            <span>Overlap-Indikator</span>
          </div>
        </div>
        <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #dee2e6; font-size: 0.8em; color: #666;">
          <strong>💡 Interpretation:</strong> Grün/Blau markierte VAE-IDs zeigen an, dass beide Tools (axe-core und LLM) 
          das gleiche DOM-Element <strong>innerhalb des gleichen WCAG-Kriteriums</strong> als problematisch identifiziert haben. 
          Dies deutet auf eine hohe Übereinstimmung in der Problemerkennung für dieses spezifische Kriterium hin.
        </div>
      </div>
    ` : '';

    return tablesHTML + legendHTML;
  }

  // Get color for impact level
  function getImpactColor(impact) {
    switch (impact?.toLowerCase()) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  }

  // Main test function
  async function runConsistencyTest(iterations = 10) {
    if (isRunning) {
      console.warn('VAE.ConsistencyTester: Test is already running');
      return;
    }

    isRunning = true;
    testResults = [];

    console.log(`VAE.ConsistencyTester: Starting consistency test with ${iterations} iterations`);
    console.log('VAE.ConsistencyTester: This will take several minutes...');
    console.log('VAE.ConsistencyTester: Each iteration will clear cache and run fresh analysis');

    const startTime = Date.now();

    try {
      // Run all iterations
      for (let i = 1; i <= iterations; i++) {
        console.log(`VAE.ConsistencyTester: === Iteration ${i}/${iterations} ===`);
        
        const iterationStart = Date.now();
        const result = await runSingleAnalysis(i);
        testResults.push(result);

        const iterationDuration = Date.now() - iterationStart;
        console.log(`VAE.ConsistencyTester: Iteration ${i} completed in ${Math.round(iterationDuration/1000)}s`);
        
        if (result.error) {
          console.warn(`VAE.ConsistencyTester: Iteration ${i} failed: ${result.error}`);
        } else {
          const axeCount = result.axeResults?.transformedCount || 0;
          const llmCount = result.llmResults?.issuesCount || 0;
          console.log(`VAE.ConsistencyTester: Iteration ${i} found ${axeCount} axe issues, ${llmCount} LLM issues`);
        }

        // Longer delay between iterations to allow system recovery
        if (i < iterations) {
          console.log(`VAE.ConsistencyTester: Waiting 5 seconds before next iteration...`);
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

      // Calculate statistics
      console.log('VAE.ConsistencyTester: Calculating statistics...');
      const statistics = calculateStatistics(testResults);

      // Generate report
      const htmlReport = generateHTMLReport(statistics, testResults);

      const totalDuration = Date.now() - startTime;
      const validIterations = testResults.filter(r => !r.error).length;
      const failedIterations = testResults.length - validIterations;

      // Display results
      console.log('VAE.ConsistencyTester: Test completed!');
      console.log(`VAE.ConsistencyTester: Total duration: ${Math.round(totalDuration/1000)}s`);
      console.log(`VAE.ConsistencyTester: Valid iterations: ${validIterations}/${iterations}`);
      console.log(`VAE.ConsistencyTester: Failed iterations: ${failedIterations}`);
      
      if (statistics.error) {
        console.error('VAE.ConsistencyTester: No valid results to analyze:', statistics.error);
      } else {
        console.log(`VAE.ConsistencyTester: Found ${statistics.summary.totalWcagCriteria} WCAG criteria`);
        console.log(`VAE.ConsistencyTester: Average issues - Axe: ${statistics.summary.averageAxeIssues}, LLM: ${statistics.summary.averageLlmIssues}`);
        console.log(`VAE.ConsistencyTester: Max variance - Axe: ${statistics.summary.maxVarianceAxe}, LLM: ${statistics.summary.maxVarianceLlm}`);
      }

      // Open report in new window
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(htmlReport);
      reportWindow.document.close();

      return {
        rawResults: testResults,
        statistics: statistics,
        htmlReport: htmlReport,
        summary: {
          totalDuration: totalDuration,
          validIterations: validIterations,
          failedIterations: failedIterations
        }
      };

    } catch (error) {
      console.error('VAE.ConsistencyTester: Test failed:', error);
      throw error;
    } finally {
      isRunning = false;
    }
  }

  // Generate report from existing results
  function regenerateReport(customResults = null) {
    const resultsToUse = customResults || testResults;
    
    if (resultsToUse.length === 0) {
      console.warn('VAE.ConsistencyTester: No test results available to generate report');
      alert('Keine Test-Ergebnisse verfügbar. Führen Sie zuerst einen Konsistenz-Test durch oder importieren Sie Ergebnisse.');
      return;
    }

    console.log(`VAE.ConsistencyTester: Regenerating report with enhanced details for ${resultsToUse.length} iterations...`);
    
    try {
      // Calculate statistics
      const statistics = calculateStatistics(resultsToUse);
      
      // Generate enhanced report
      const htmlReport = generateHTMLReport(statistics, resultsToUse);
      
      // Open report in new window
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(htmlReport);
      reportWindow.document.close();
      
      console.log('VAE.ConsistencyTester: Enhanced report generated successfully');
      
      return {
        statistics: statistics,
        htmlReport: htmlReport,
        rawResults: resultsToUse
      };
      
    } catch (error) {
      console.error('VAE.ConsistencyTester: Error generating report:', error);
      alert('Fehler beim Generieren des Reports: ' + error.message);
    }
  }

  // Import and process test results from JSON
  function importResults() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = function(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = function(e) {
        try {
          const importedData = JSON.parse(e.target.result);
          
          // Validate imported data structure
          if (!importedData.rawResults || !Array.isArray(importedData.rawResults)) {
            throw new Error('Invalid file format: rawResults array not found');
          }
          
          console.log(`VAE.ConsistencyTester: Imported ${importedData.rawResults.length} test results`);
          
          // Ask user if they want to replace current results or generate report from imported data
          const replace = confirm(
            `${importedData.rawResults.length} Test-Ergebnisse gefunden.\n\n` +
            'OK = Aktuelle Ergebnisse ersetzen\n' +
            'Abbrechen = Nur Report aus importierten Daten generieren'
          );
          
          if (replace) {
            testResults = importedData.rawResults;
            console.log('VAE.ConsistencyTester: Current results replaced with imported data');
          }
          
          // Generate report from imported data
          regenerateReport(importedData.rawResults);
          
        } catch (error) {
          console.error('VAE.ConsistencyTester: Error importing results:', error);
          alert('Fehler beim Importieren der Ergebnisse: ' + error.message);
        }
      };
      
      reader.readAsText(file);
    };
    
    input.click();
  }

  // Export raw data as JSON
  function exportResults() {
    if (testResults.length === 0) {
      console.warn('VAE.ConsistencyTester: No test results to export');
      return;
    }

    const statistics = calculateStatistics(testResults);
    const exportData = {
      metadata: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        iterations: testResults.length
      },
      rawResults: testResults,
      statistics: statistics
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vae-consistency-test-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Debug function to check Supabase database state
  async function debugSupabaseState(taskId, pageId) {
    console.log(`=== SUPABASE DEBUG for Task ${taskId}, Page ${pageId} ===`);
    
    try {
      // Check if we can access the Supabase client
      if (!window.VAE || !window.VAE.Config || !window.VAE.Config.api) {
        console.error('VAE.ConsistencyTester: No VAE config available for Supabase debug');
        return;
      }
      
      const token = window.VAE.Config.client.token;
      const apiEndpoint = window.VAE.Config.api.endpoint;
      
      if (!token || !apiEndpoint) {
        console.error('VAE.ConsistencyTester: No token or API endpoint for Supabase debug');
        return;
      }
      
      console.log(`VAE.ConsistencyTester: Using API endpoint: ${apiEndpoint}`);
      console.log(`VAE.ConsistencyTester: Using token (first 10 chars): ${token.substring(0, 10)}...`);
      
      // Check task data (might be deleted already)
      console.log('--- Checking Task Data (might be deleted) ---');
      try {
        const taskResponse = await fetch(`${apiEndpoint}/${taskId}/results`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log(`Task response status: ${taskResponse.status}`);
        if (taskResponse.ok) {
          const taskData = await taskResponse.json();
          console.log('Task Data:', taskData);
          console.log(`Task Status: ${taskData.status}`);
          console.log(`Task Issues Count: ${taskData.total_issues_found || 0}`);
          console.log(`Task Issues Array Length: ${taskData.issues?.length || 0}`);
          if (taskData.issues && taskData.issues.length > 0) {
            console.log('Sample Task Issues:', taskData.issues.slice(0, 3));
          }
        } else {
          const errorText = await taskResponse.text();
          console.log('Task fetch failed:', taskResponse.status, taskResponse.statusText, errorText);
        }
      } catch (error) {
        console.error('Error fetching task data:', error);
      }
      
      // Check page data
      console.log('--- Checking Page Data ---');
      try {
        const pageResponse = await fetch(`${apiEndpoint}/page-results/${pageId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log(`Page response status: ${pageResponse.status}`);
        console.log(`Page response headers:`, Object.fromEntries([...pageResponse.headers.entries()]));
        
        if (pageResponse.ok) {
          const pageData = await pageResponse.json();
          console.log('Page Data (full response):', pageData);
          console.log(`Page Issues Count: ${pageData.total_issues_found || 0}`);
          console.log(`Page Issues Array Length: ${pageData.issues?.length || 0}`);
          console.log(`Page Status: ${pageData.status || 'unknown'}`);
          console.log(`Page URL: ${pageData.url || 'unknown'}`);
          console.log(`Page Last Analyzed: ${pageData.last_analyzed || 'unknown'}`);
          
          if (pageData.issues && pageData.issues.length > 0) {
            console.log('Sample Page Issues:', pageData.issues.slice(0, 3));
            console.log('Page Issues Structure Check:');
            pageData.issues.slice(0, 3).forEach((issue, index) => {
              console.log(`Issue ${index}:`, {
                wcagCriterion: issue.wcagCriterion || issue.wcag_criterion || 'missing',
                type: issue.type || issue.id || 'missing',
                impact: issue.impact || 'missing',
                selector: issue.selector || 'missing',
                message: issue.message || issue.description || 'missing',
                source: issue.source || 'missing'
              });
            });
          } else {
            console.warn('No issues found in page data');
            if (pageData.issues) {
              console.log('Issues array exists but is empty:', pageData.issues);
            } else {
              console.log('No issues property in page data');
            }
          }
        } else {
          const errorText = await pageResponse.text();
          console.error('Page fetch failed:', pageResponse.status, pageResponse.statusText, errorText);
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      }
      
      // Test direct API call to see raw response
      console.log('--- Testing Direct API Call ---');
      try {
        const directResponse = await fetch(`${apiEndpoint}/page-results/${pageId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        console.log(`Direct API call status: ${directResponse.status}`);
        const responseText = await directResponse.text();
        console.log(`Direct API call response (raw text):`, responseText);
        
        try {
          const parsedResponse = JSON.parse(responseText);
          console.log(`Direct API call response (parsed):`, parsedResponse);
        } catch (parseError) {
          console.error('Failed to parse direct API response:', parseError);
        }
      } catch (error) {
        console.error('Error in direct API call:', error);
      }
      
    } catch (error) {
      console.error('VAE.ConsistencyTester: Error in Supabase debug:', error);
    }
    
    console.log('=== END SUPABASE DEBUG ===');
  }

  // Generate WCAG criteria vs iterations matrix table
  function generateWcagIterationMatrix(testResults = []) {
    const validResults = testResults.filter(r => !r.error);
    
    if (validResults.length === 0) {
      return '<p>Keine gültigen Iterationen für Matrix-Ansicht verfügbar.</p>';
    }

    // Collect all WCAG criteria found across all iterations
    const allWcagCriteria = new Set();
    validResults.forEach(result => {
      if (result.axeResults) {
        Object.keys(result.axeResults.wcagStats).forEach(wcag => allWcagCriteria.add(wcag));
      }
      if (result.llmResults) {
        Object.keys(result.llmResults.wcagStats).forEach(wcag => allWcagCriteria.add(wcag));
      }
    });

    const sortedCriteria = Array.from(allWcagCriteria).sort((a, b) => a.localeCompare(b));

    // Generate matrix data
    const matrixData = sortedCriteria.map(wcag => {
      const row = {
        criterion: wcag,
        title: WCAG_CRITERIA[wcag] || 'Unknown',
        iterations: []
      };

      validResults.forEach((result, index) => {
        const axeCount = result.axeResults?.wcagStats[wcag]?.count || 0;
        const llmCount = result.llmResults?.wcagStats[wcag]?.count || 0;
        const totalCount = axeCount + llmCount;

        row.iterations.push({
          iteration: index + 1,
          axe: axeCount,
          llm: llmCount,
          total: totalCount
        });
      });

      return row;
    });

    // Calculate totals per iteration
    const iterationTotals = validResults.map((result, index) => {
      const axeTotal = result.axeResults?.transformedCount || 0;
      const llmTotal = result.llmResults?.issuesCount || 0;
      return {
        iteration: index + 1,
        axe: axeTotal,
        llm: llmTotal,
        total: axeTotal + llmTotal
      };
    });

    return `
      <div style="margin-bottom: 2rem;">
        <h3 style="margin-bottom: 1rem;">📊 WCAG-Kriterien vs. Iterationen Matrix</h3>
        <p style="margin-bottom: 1rem; color: #666; font-size: 0.9em;">
          Diese Tabelle zeigt die Anzahl der gefundenen Issues pro WCAG-Kriterium und Iteration. 
          <strong>Axe</strong> = axe-core Issues, <strong>LLM</strong> = LLM-gefundene Issues, <strong>Total</strong> = Summe beider.
        </p>
        
        <div style="overflow-x: auto; margin-bottom: 2rem;">
          <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 0.85em;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="text-align: left; min-width: 80px; position: sticky; left: 0; background: #f8f9fa; z-index: 10;">WCAG</th>
                <th style="text-align: left; min-width: 200px; position: sticky; left: 80px; background: #f8f9fa; z-index: 10;">Kriterium</th>
                ${validResults.map((_, index) => `
                  <th style="text-align: center; min-width: 80px;">
                    <div style="writing-mode: vertical-rl; text-orientation: mixed; height: 60px; display: flex; align-items: center; justify-content: center;">
                      Iteration ${index + 1}
                    </div>
                  </th>
                `).join('')}
                <th style="text-align: center; min-width: 60px; background: #e9ecef;">
                  <div style="writing-mode: vertical-rl; text-orientation: mixed; height: 60px; display: flex; align-items: center; justify-content: center;">
                    Σ Total
                  </div>
                </th>
                <th style="text-align: center; min-width: 60px; background: #e9ecef;">
                  <div style="writing-mode: vertical-rl; text-orientation: mixed; height: 60px; display: flex; align-items: center; justify-content: center;">
                    Ø Avg
                  </div>
                </th>
                <th style="text-align: center; min-width: 60px; background: #e9ecef;">
                  <div style="writing-mode: vertical-rl; text-orientation: mixed; height: 60px; display: flex; align-items: center; justify-content: center;">
                    Varianz
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              ${matrixData.map(row => {
                const totalSum = row.iterations.reduce((sum, iter) => sum + iter.total, 0);
                const totalAvg = Math.round((totalSum / row.iterations.length) * 10) / 10;
                const totalValues = row.iterations.map(iter => iter.total);
                const totalVariance = Math.max(...totalValues) - Math.min(...totalValues);
                
                return `
                  <tr>
                    <td style="font-weight: bold; position: sticky; left: 0; background: white; z-index: 5;">${row.criterion}</td>
                    <td style="position: sticky; left: 80px; background: white; z-index: 5; max-width: 200px; word-wrap: break-word;">${row.title}</td>
                    ${row.iterations.map(iter => `
                      <td style="text-align: center; ${iter.total === 0 ? 'background: #f8f9fa; color: #999;' : iter.total > 5 ? 'background: #fff3cd;' : ''}">
                        ${iter.total === 0 ? '—' : `
                          <div style="font-weight: bold; color: #333;">${iter.total}</div>
                          <div style="font-size: 0.75em; color: #666;">
                            ${iter.axe > 0 ? `A:${iter.axe}` : ''}${iter.axe > 0 && iter.llm > 0 ? ' ' : ''}${iter.llm > 0 ? `L:${iter.llm}` : ''}
                          </div>
                        `}
                      </td>
                    `).join('')}
                    <td style="text-align: center; font-weight: bold; background: #e9ecef;">${totalSum}</td>
                    <td style="text-align: center; background: #e9ecef;">${totalAvg}</td>
                    <td style="text-align: center; background: #e9ecef; ${totalVariance > 2 ? 'color: #d63384; font-weight: bold;' : ''}">${totalVariance}</td>
                  </tr>
                `;
              }).join('')}
              
              <!-- Totals row -->
              <tr style="background: #e9ecef; font-weight: bold; border-top: 2px solid #adb5bd;">
                <td style="position: sticky; left: 0; background: #e9ecef; z-index: 5;">TOTAL</td>
                <td style="position: sticky; left: 80px; background: #e9ecef; z-index: 5;">Alle Kriterien</td>
                ${iterationTotals.map(iter => `
                  <td style="text-align: center;">
                    <div style="font-weight: bold; color: #333;">${iter.total}</div>
                    <div style="font-size: 0.75em; color: #666;">
                      A:${iter.axe} L:${iter.llm}
                    </div>
                  </td>
                `).join('')}
                <td style="text-align: center; background: #d1ecf1;">
                  ${iterationTotals.reduce((sum, iter) => sum + iter.total, 0)}
                </td>
                <td style="text-align: center; background: #d1ecf1;">
                  ${Math.round((iterationTotals.reduce((sum, iter) => sum + iter.total, 0) / iterationTotals.length) * 10) / 10}
                </td>
                <td style="text-align: center; background: #d1ecf1;">
                  ${Math.max(...iterationTotals.map(iter => iter.total)) - Math.min(...iterationTotals.map(iter => iter.total))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style="background: #f8f9fa; padding: 1rem; border-radius: 0.5rem; font-size: 0.85em;">
          <h4 style="margin-top: 0; color: #495057;">📖 Legende:</h4>
          <ul style="margin-bottom: 0; columns: 2; column-gap: 2rem;">
            <li><strong>A:</strong> Axe-core Issues</li>
            <li><strong>L:</strong> LLM Issues</li>
            <li><strong>—:</strong> Keine Issues gefunden</li>
            <li><strong>Gelb hinterlegt:</strong> Mehr als 5 Issues</li>
            <li><strong>Varianz rot:</strong> Hohe Schwankung (>2)</li>
            <li><strong>Sticky Headers:</strong> WCAG + Kriterium bleiben beim Scrollen sichtbar</li>
          </ul>
        </div>
      </div>
    `;
  }

  // Analyze VAE ID overlaps between axe-core and LLM results
  function analyzeVaeIdOverlaps(axeIssues, llmIssues) {
    const axeVaeIds = new Set();
    const llmVaeIds = new Set();
    
    axeIssues.forEach(issue => {
      const vaeId = issue.vaeId;
      if (vaeId) axeVaeIds.add(vaeId);
    });
    
    llmIssues.forEach(issue => {
      const vaeId = issue.vaeId;
      if (vaeId) llmVaeIds.add(vaeId);
    });
    
    const overlap = new Set([...axeVaeIds].filter(id => llmVaeIds.has(id)));
    const axeOnly = new Set([...axeVaeIds].filter(id => !llmVaeIds.has(id)));
    const llmOnly = new Set([...llmVaeIds].filter(id => !axeVaeIds.has(id)));
    
    return {
      axeVaeIds: Array.from(axeVaeIds),
      llmVaeIds: Array.from(llmVaeIds),
      overlap: Array.from(overlap),
      axeOnly: Array.from(axeOnly),
      llmOnly: Array.from(llmOnly),
      overlapPercentage: axeVaeIds.size > 0 ? Math.round((overlap.size / axeVaeIds.size) * 100) : 0
    };
  }

  // Generate VAE ID analysis HTML
  function generateVaeIdAnalysisHTML(vaeIdAnalysis) {
    if (!vaeIdAnalysis || !vaeIdAnalysis.iterationOverlaps) {
      return '<p>Keine VAE-ID-Analyse-Daten verfügbar.</p>';
    }

    const overlaps = vaeIdAnalysis.iterationOverlaps;
    
    return `
      <div style="margin-bottom: 2rem;">
        <h3 style="margin-bottom: 1rem;">🎯 Element-Identifikation: Axe-core vs. LLM</h3>
        <p style="margin-bottom: 1rem; color: #666; font-size: 0.9em;">
          Diese Analyse zeigt, inwieweit axe-core und LLM die gleichen DOM-Elemente (VAE-IDs) als problematisch identifizieren.
          Eine hohe Überschneidung deutet auf konsistente Problemerkennung hin.
        </p>
        
        <!-- Summary Statistics -->
        <div style="background: #f8f9fa; padding: 1rem; border-radius: 0.5rem; margin-bottom: 1.5rem;">
          <h4 style="margin-top: 0; color: #495057;">📊 Gesamtstatistik</h4>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;">
            <div>
              <strong>Durchschnittliche Überschneidung:</strong><br>
              <span style="font-size: 1.2em; color: #1976d2; font-weight: bold;">${vaeIdAnalysis.averageOverlapPercentage}%</span>
            </div>
            <div>
              <strong>Überschneidungsbereich:</strong><br>
              <span style="color: #666;">${vaeIdAnalysis.minOverlapPercentage}% - ${vaeIdAnalysis.maxOverlapPercentage}%</span>
            </div>
            <div>
              <strong>Einzigartige Axe-core Elemente:</strong><br>
              <span style="color: #f57c00; font-weight: bold;">${vaeIdAnalysis.totalUniqueAxeVaeIds}</span>
            </div>
            <div>
              <strong>Einzigartige LLM Elemente:</strong><br>
              <span style="color: #388e3c; font-weight: bold;">${vaeIdAnalysis.totalUniqueLlmVaeIds}</span>
            </div>
          </div>
        </div>
        
        <!-- Iteration-by-Iteration Analysis -->
        <h4 style="margin-bottom: 1rem; color: #495057;">📋 Iteration-für-Iteration Analyse</h4>
        <div style="overflow-x: auto;">
          <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 0.9em;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="text-align: center;">Iteration</th>
                <th style="text-align: center;">Axe-core<br>Elemente</th>
                <th style="text-align: center;">LLM<br>Elemente</th>
                <th style="text-align: center;">Gemeinsame<br>Elemente</th>
                <th style="text-align: center;">Überschneidung<br>%</th>
                <th style="text-align: center;">Nur Axe-core</th>
                <th style="text-align: center;">Nur LLM</th>
                <th style="text-align: left; min-width: 200px;">Gemeinsame VAE-IDs (Auswahl)</th>
              </tr>
            </thead>
            <tbody>
              ${overlaps.map((overlap, index) => {
                const iteration = index + 1;
                const overlapColor = overlap.overlapPercentage >= 50 ? '#d4edda' : 
                                   overlap.overlapPercentage >= 25 ? '#fff3cd' : '#f8d7da';
                
                return `
                  <tr style="background: ${overlapColor};">
                    <td style="text-align: center; font-weight: bold;">${iteration}</td>
                    <td style="text-align: center;">${overlap.axeVaeIds.length}</td>
                    <td style="text-align: center;">${overlap.llmVaeIds.length}</td>
                    <td style="text-align: center; font-weight: bold; color: #1976d2;">${overlap.overlap.length}</td>
                    <td style="text-align: center; font-weight: bold;">
                      <span style="color: ${overlap.overlapPercentage >= 50 ? '#28a745' : overlap.overlapPercentage >= 25 ? '#ffc107' : '#dc3545'};">
                        ${overlap.overlapPercentage}%
                      </span>
                    </td>
                    <td style="text-align: center; color: #f57c00;">${overlap.axeOnly.length}</td>
                    <td style="text-align: center; color: #388e3c;">${overlap.llmOnly.length}</td>
                    <td style="font-family: monospace; font-size: 0.8em; max-width: 200px; word-break: break-all;">
                      ${overlap.overlap.length > 0 ? 
                        overlap.overlap.slice(0, 3).join(', ') + 
                        (overlap.overlap.length > 3 ? ` (+${overlap.overlap.length - 3})` : '') 
                        : '—'}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <!-- Interpretation Guide -->
        <div style="background: #e8f4fd; padding: 1rem; border-radius: 0.5rem; margin-top: 1.5rem;">
          <h4 style="margin-top: 0; color: #495057;">💡 Interpretation der Ergebnisse:</h4>
          <div style="columns: 2; column-gap: 2rem; font-size: 0.9em;">
            <div style="margin-bottom: 1rem;">
              <strong style="color: #28a745;">Hohe Überschneidung (≥50%):</strong><br>
              Beide Tools identifizieren ähnliche Problemelemente. Deutet auf zuverlässige Erkennung hin.
            </div>
            <div style="margin-bottom: 1rem;">
              <strong style="color: #ffc107;">Mittlere Überschneidung (25-49%):</strong><br>
              Teilweise Übereinstimmung. Tools finden unterschiedliche Aspekte der gleichen Probleme.
            </div>
            <div style="margin-bottom: 1rem;">
              <strong style="color: #dc3545;">Niedrige Überschneidung (<25%):</strong><br>
              Tools identifizieren größtenteils verschiedene Elemente. Kann auf unterschiedliche Erkennungsansätze hindeuten.
            </div>
            <div style="margin-bottom: 1rem;">
              <strong style="color: #f57c00;">Nur Axe-core:</strong><br>
              Elemente, die nur von axe-core gefunden wurden. Möglicherweise technische/strukturelle Issues.
            </div>
            <div style="margin-bottom: 1rem;">
              <strong style="color: #388e3c;">Nur LLM:</strong><br>
              Elemente, die nur vom LLM gefunden wurden. Möglicherweise kontextuelle/semantische Issues.
            </div>
            <div>
              <strong>Konsistenz-Indikator:</strong><br>
              Geringe Schwankung der Überschneidung zwischen Iterationen deutet auf stabile Erkennung hin.
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Public API
  return {
    runConsistencyTest,
    regenerateReport,
    importResults,
    exportResults,
    getLastResults: () => testResults,
    isRunning: () => isRunning
  };
})();

console.log('VAE: ConsistencyTester module loaded'); 